import React, { useRef } from 'react';
import { AvTooltip, FieldListObject, fieldTypeIconsMap } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Button, Collapse, List, Typography, useTheme } from '@mui/material';
import { useAvContext } from '../../context/AvContextProvider';
import { sortByStringField } from '../../utils/utils';
import { MappingItem } from '../Sources/Mapping/MappingItem';
import { DragItem } from '../Sources/Mapping/types';

type EntityItemValue = Pick<FieldListObject, 'title' | 'value'> & Partial<Omit<FieldListObject, 'title' | 'value'>>;

export type DragItemEntity = Omit<EntityItemValue, 'type'> & {
  type: FieldListObject['type'] | number;
};
interface EntityProps {
  displayName: string;
  expanded?: boolean;
  setExpand: (displayName: string) => void;
  collapseProps: any;
  onItemDoubleClick?: (dragItem: DragItemEntity) => void;
  onItemClick?: (dragItem: DragItemEntity) => void;
  onDragStart: (dragItem: DragItemEntity) => void;
  items: EntityItemValue[];
  nameMap?: { [key: string]: string };
  usedFields?: string[];
  type?: string;
  headerAdornment?: React.ReactNode;
  shouldDisableItem?: (item: { title: string; value: string }) => { disabled: boolean; disabledTooltip: string };
}
const EntityItem: React.FC<EntityProps> = ({
  displayName,
  expanded = false,
  setExpand,
  collapseProps,
  onItemDoubleClick,
  onItemClick,
  onDragStart,
  items: originalItems,
  usedFields,
  type,
  headerAdornment,
  shouldDisableItem,
}) => {
  const theme = useTheme();
  const { fieldTypeMap } = useAvContext().accountEntities;
  const ref = useRef<any>(undefined);
  const getDragItem = (field: EntityItemValue): DragItemEntity => ({ type: DragItem.Entity, ...field });

  const items = sortByStringField(originalItems, 'title');
  const getType = (pathAlias, CustomType) => {
    const type = CustomType || fieldTypeMap[pathAlias];
    return fieldTypeIconsMap()[Array.isArray(type) ? JSON.stringify(type) : type] || type;
  };

  const entityItemText = `${displayName} (${items.length})`;
  const renderItem = ({ value, title, pathAlias, ...props }: EntityItemValue) => {
    const disabledState = shouldDisableItem?.({ value, title }) ?? { disabled: false, disabledTooltip: '' };
    const dragItem = getDragItem({ value, title, pathAlias, ...props });
    return (
      <AvTooltip key={value} title={disabledState.disabledTooltip} disabled={!disabledState.disabled} sx={{ width: '100%' }}>
        <Box sx={disabledState.disabled ? { color: 'var(--color-content-base-disabled)' } : undefined}>
          <MappingItem
            size="small"
            isUsed={usedFields?.includes(value)}
            draggable={!disabledState.disabled}
            label={
              <>
                {getType(value, type)}
                <Box sx={{ flex: 1, minWidth: 0, ...ellipsis }}>
                  <AvTooltip
                    muiProps={{
                      placement: 'top',
                      ...(disabledState.disabled && {
                        disableHoverListener: true,
                        disableFocusListener: true,
                        disableTouchListener: true,
                      }),
                    }}>
                    {title || value}
                  </AvTooltip>
                </Box>
              </>
            }
            onDoubleClick={disabledState.disabled ? undefined : () => (onItemDoubleClick ? onItemDoubleClick(dragItem) : undefined)}
            onClick={disabledState.disabled || !onItemClick ? undefined : () => onItemClick(dragItem)}
            onDragStart={disabledState.disabled ? undefined : () => onDragStart(dragItem)}
          />
        </Box>
      </AvTooltip>
    );
  };
  return (
    <Box key={displayName}>
      <Box sx={{ ...flex.justifyBetweenCenter }}>
        <Button ref={ref} onClick={() => setExpand(displayName)} disabled={!items.length}>
          <ArrowDown
            style={{
              opacity: !items.length ? 0.4 : 1,
              color: 'var(--color-content-base-secondary)',
              transform: `rotate(${expanded ? 0 : -90}deg)`,
              transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
            }}
          />
          <Typography variant="h7" sx={{ ...flex.row, width: '240px', opacity: !items.length ? 0.4 : 1 }}>
            <AvTooltip>{entityItemText}</AvTooltip>
          </Typography>
          {headerAdornment}
        </Button>
      </Box>
      <Box sx={{ ...flex.col, gap: 2 }}>
        <Collapse in={!!(expanded && items?.length)} {...collapseProps}>
          <List sx={{ gap: 1, ...flex.col }}>{items.map(renderItem)}</List>
        </Collapse>
      </Box>
    </Box>
  );
};

export default EntityItem;
